<template>
  <form @submit="onSubmit">
    <vl-form-column v-vl-spacer:bottom.small>
      <PbsSelectField
        name="codeAttribute"
        :label="t('codeListAttributeMapping.detail.code')"
        :placeholder="$t('codeListAttributeMapping.detail.code')"
        :options="data.attributeOptions"
        :custom-label="entityAttributeLabel"
      />
    </vl-form-column>
    <vl-form-column v-vl-spacer:bottom.small>
      <PbsSelectField
        name="nameAttribute"
        :label="t('codeListAttributeMapping.detail.name')"
        :placeholder="$t('codeListAttributeMapping.detail.name')"
        :options="data.attributeOptions"
        :custom-label="entityAttributeLabel"
      />
    </vl-form-column>
    <vl-form-column v-vl-spacer:bottom.small>
      <PbsSelectField
        name="descriptionAttribute"
        :label="t('codeListAttributeMapping.detail.description')"
        :placeholder="$t('codeListAttributeMapping.detail.description')"
        :options="data.attributeOptions"
        :custom-label="entityAttributeLabel"
      />
    </vl-form-column>
  </form>
</template>
<script lang="ts" setup>
import { onMounted, reactive } from 'vue';
import { useForm } from 'vee-validate';
import { object, string } from 'yup';
import { useI18n } from 'vue-i18n';
import { CodeListAttributeMappingDto, EntityAttributeDetailDto, EntityModelDetailDto } from '@/api/backoffice/clients';
import PbsSelectField from '@/components/forms/PbsSelectField.vue';
import { useEntityAttributeStore } from '@/modules/configuration/store/entity-attribute/entity-attribute.store';
import { useCodeListAttributeMappingDetailStore } from '@/modules/configuration/store/code-list-attribute-mapping/code-list-attribute-mapping-detail.store';

const { t } = useI18n();

const store = useCodeListAttributeMappingDetailStore();
const entityAttributeStore = useEntityAttributeStore();

const props = defineProps<{
  detail: CodeListAttributeMappingDto;
  model: EntityModelDetailDto;
}>();

const validationSchema = object({
  codeAttribute: object().required(
    t('validations.messages.required', {
      _field_: t('codeListAttributeMapping.detail.code')
    })
  ),
  nameAttribute: object().required(
    t('validations.messages.required', {
      _field_: t('codeListAttributeMapping.detail.name')
    })
  ),
  descriptionAttribute: object().required(
    t('validations.messages.required', {
      _field_: t('codeListAttributeMapping.detail.description')
    })
  )
});

const { handleSubmit, resetForm } = useForm({
  validationSchema
});

const data = reactive({
  attributeOptions: [] as EntityAttributeDetailDto[]
});

onMounted(() => {
  setCodeAttributeOptions();

  resetForm({
    values: getInitialFormValues()
  });
});

const getInitialFormValues = () => {
  const values = {
    codeAttribute: props.detail?.attributeMappings['Code'],
    nameAttribute: props.detail?.attributeMappings['Name'],
    descriptionAttribute: props.detail?.attributeMappings['Description']
  };

  return values;
};

const entityAttributeLabel = (entityAttribute: EntityAttributeDetailDto) => {
  if (!entityAttribute.id) {
    return '';
  }
  return `${entityAttribute.code} (${entityAttribute.label})`;
};

const setCodeAttributeOptions = () => {
  data.attributeOptions = entityAttributeStore.byModel(props.model.id);
};

const onSuccess = async (values) => {
  await store.save(props.model.id, {
    attributeMappings: {
      Code: values.codeAttribute.id,
      Name: values.nameAttribute.id,
      Description: values.descriptionAttribute.id
    }
  });
};

const onInvalidSubmit = ({ values, errors, results }) => {
  // console.log(values); // current form values
  // console.log(errors); // a map of field names and their first error message
  // console.log(results); // a detailed map of field names and their validation results
};

const onSubmit = handleSubmit(onSuccess, onInvalidSubmit);

defineExpose({ onSubmit });
</script>
